<article class=" ">
    <header>

        <div class=" ">
            <ul class="list-inline d-flex justify-content-between mb-0">
                <li class="list-inline-item">
                    <h1 class="">Market Benchmarks</h1>
                </li>
                <li class="list-inline-item">
                    <button class="btn btn-icon btn-light-info mx-1" (click)="printPage()">
                        <i class="ti ti-printer"></i>
                    </button>
                    <button aria-label="Copy link" class="btn btn-icon btn-light-info" (click)="copyFullPageText()">
                        <i class="ti ti-copy"></i>
                    </button>
                </li>
            </ul>
        </div>
    </header>
    <div class="">
        <div class="card-body p-2">
            <div class="table-border-style">
                <p class="mt-1">Xeneta organizes its freight rate benchmark data into 5 groups to help you compare your
                    rates against
                    different segments of the ocean market. These groups are as follows:</p>
                <div class="table-responsive">
                    <table class="table table-striped">
                        <tbody>
                            <tr>
                                <td style="width: 26.6406%;">
                                    <strong>Market High</strong>
                                    <br>
                                </td>
                                <td style="width: 73.3594%;">
                                    The rate seen at the
                                    <strong>97.5&nbsp;</strong>
                                    <strong>percentile</strong>
                                    of the market. In other words, only 2.5% of the marketn rates will be above the
                                    rates seen here.<br>
                                </td>
                            </tr>
                            <tr>
                                <td style="width: 26.6406%;">
                                    <strong>Market Mid-High</strong>
                                    <br>
                                </td>
                                <td style="width: 73.3594%;">
                                    The rate seen at the
                                    <strong>75 percentile</strong>
                                    of the market. In other words, 25% of market rates will be above the rates seen
                                    here.
                                    <br>
                                </td>
                            </tr>
                            <tr>
                                <td style="width: 26.6406%;">
                                    <strong>Market Average</strong>
                                    <br>
                                </td>
                                <td style="width: 73.3594%;">The average rate seen on the market.<br></td>
                            </tr>
                            <tr>
                                <td style="width: 26.6406%;" width="150"><strong>Market Mid-Low</strong><br></td>
                                <td style="width: 73.3594%;">The rate seen at the&nbsp;<strong>25 percentile</strong>
                                    of the market. In other words, 75% of market rates will be above the rates seen
                                    here.<br>
                                </td>
                            </tr>
                            <tr>
                                <td style="width: 26.6406%;"><strong>Market Low</strong></td>
                                <td style="width: 73.3594%;">The rate seen at the <strong>2.5 percentile</strong>
                                    of the market. In other words, 97.5% of market rates will be above the rates seen
                                    here.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <p>
                In very simple terms, you can think of these benchmarks as windows into different sections of market
                rates for specific trade lanes or corridors.
            </p>
            <h2 class="my-4">How Xeneta Creates Benchmarks</h2>
            <p class="my-4">
                Xeneta creates a benchmark by first requesting a list of rates that are comparable to your rates from
                its
                database, and then aggregating that list of rates into a single number based on your selected
                benchmarking group.
            </p>
            <p class="my-4">
                As an example, let's assume that you want to benchmark your rates for the Shanghai–Oslo trade lane on
                today's date. To provide you with a single benchmark rate, Xeneta needs to perform a number of
                calculations "behind the scenes".
            </p>
            <p class="my-4">
                For our example, let's assume that the following 10 rates are available to Xeneta for the Shanghai–Oslo
                trade lane. Notice that the rates are provided in order of increasing rate.
            </p>
            <p class="my-4">
                <img src="https://cdn.elev.io/file/uploads/tf_fttuKU75UzQGjhddt1-xv5BkneNrEFXMUroCnHEo/6AzS9hq6vZOq5CvKyqvvkHjSW77px8mQ_4h7JPeaoCI/1-5ik.png"
                    alt="1.png" class="fr-fic fr-dii">
            </p>
            <p class="my-4">
                These 10 rates represent the different rates that have been paid to ship cargo from Shanghai to Oslo.
                Any
                individual rate by itself will not say anything meaningful about the freight market as a whole. However,
                if we take all these rates together, we can calculate their average, as well as the lowest and highest
                rates for the trade lane.
            </p>
            <blockquote class="my-4 p-3 bg-gray-300 color-block">
                <p class="m-0">
                    The example described here is shown for illustration purposes only. It does not reflect real-world
                    freight rates on the <strong>Shanghai–Oslo</strong> trade lane, and nor does it use Xeneta's data
                    quality considerations.
                </p>
            </blockquote>
            <h2>Market Average</h2>
            <p>
                The market average rate is calculated by taking the arithmetic mean of all the rates available in our
                database.
            </p>
            <blockquote class="my-4 p-3 bg-yellow-100 color-block">
                Keep in mind that since <strong>Market Average</strong> is calculated
                as an actual arithmetic mean (while other benchmarks are based on percentiles), it's entirely possible
                to see rare cases where, for example, <strong>Market Average</strong> is actually higher than
                <strong>Mid-High</strong>.
            </blockquote>
            <p>
                Going back to our previous example, the arithmetic mean for these 10 rates will be calculated as
                follows:
            </p>
            <p><img src="https://cdn.elev.io/file/uploads/EdeXO2xZbXAkWmnWf3_AX6UBgb7MhK9x3otfVJmCadk/NFhBh7Crcj69mYbJSUvNj9r02T3fxhFrJyJIdkNhg1w/2-KvA.png"
                    alt="2.png" class="fr-fic fr-dii"></p>
            <p>
                The resulting number will then be rounded up or down (in our case, to $648), and then used as the
                <strong>Market Average</strong> value for you to benchmark your rates on Shanghai-Oslo against.
            </p>
            <h2>Market Low and Market High</h2>
            <p>
                The market low and market high benchmarks are calculated by looking at the rates at the
                <strong>2.5</strong> and <strong>97.5 percentiles</strong> of the trade lane data available to Xeneta.
                This is the same calculation that you can perform in Excel with the <code>PERCENTILE.INC</code>
                function.
            </p>
            <p>Plotted on a line graph, the market low and market high for our example rates will look like this:</p>
            <p><img src="https://cdn.elev.io/file/uploads/SATZaHBVZxqN0bxWxfJh_p9SDmavgKtukoxDV7WaOCE/NtSeAi17H9c64jxTGtTfy-r3OQ54PaqS34etOUS25HA/market_high_low-5xo.png"
                    alt="market_high_low.png" class="fr-fic fr-dii"></p>
            <p>
                Since our example only has 10 rates, the 2.5 percentile is located between rate 1 and rate 2, while the
                97.5 percentile is located between rate 9 and 10.
            </p>
            <p>To provide a value for benchmarking, Xeneta will take a linear interpolation between two rates to assign
                an appropriate value for the low and high market segments. This puts the Market Low value in our example
                at $423, while and the Market High is at $939.</p>
            <h2>Market Mid-Low and Mid-High</h2>
            <p>The market mid-low and market mid-high benchmarks are calculated by looking at the values at the 25 and
                75 percentiles of the trade lane rate data available to Xeneta.</p>
            <p>Plotted on a line graph, the market mid-low and mid-high for our example rates will look like this:</p>
            <p><img src="https://cdn.elev.io/file/uploads/kdFVyOigY3r_EknkSv_ERHcK0wjlRhpy51Ajm7jf0AM/OGMrkc9QA4DYt6q6o-_paKYw0Ri-9Xl9ilpnG0Eudr4/b-t8Y.png"
                    alt="b.png" class="fr-fic fr-dii"></p>
            <p>As we can see, the mid-low segment is located between rate 3 and rate 4, while the Mid-High segment is
                located between rate 7 and 8, with the Market Mid-Low and Mid-High values sitting at $531 and $698
                respectively.</p>
            <h2>All Market Segments</h2>
            <p>Having calculated five different benchmarking positions for our rates on the Shanghai-Oslo trade lane, we
                can see their distribution on a single chart like this:</p>
            <p><img src="https://cdn.elev.io/file/uploads/UcZ1Z5MP80H2no2D4AQx5RmGRAWeuvKt_hISaCBEyxI/moK0XovZS1l8HpeYfpjSvsHWaULp-PK-UJhI2YWrNSY/c-hTE.png"
                    alt="c.png" class="fr-fic fr-dii"></p>
        </div>
    </div>

</article>